type BaseRow = {
  id: number;
  position: number;
  is_excluded: boolean;
};

type DropCalculationParams<T extends BaseRow> = {
  draggedRow: T;
  dropTargetRow: T & { totalRowsInSection: number };
};

type DropCalculationResult = {
  shouldInsertBelow: boolean;
  targetPosition: number;
};

/**
 * calculates where to insert a dragged row relative to a drop target
 * handles both same-section and cross-section drops
 */
export const calculateDropPosition = <T extends BaseRow>({
  draggedRow,
  dropTargetRow,
}: DropCalculationParams<T>): DropCalculationResult => {
  const sameSect = draggedRow.is_excluded === dropTargetRow.is_excluded;
  const isLastInSection =
    dropTargetRow.position === dropTargetRow.totalRowsInSection - 1;
  const isFirstInSection = dropTargetRow.position === 0;

  // determine if we're inserting above or below the target
  let shouldInsertBelow = false;
  if (sameSect) {
    // within same section, use position comparison
    shouldInsertBelow = draggedRow.position < dropTargetRow.position;
  } else {
    // cross-section: Included (is_excluded=false) is visually above Excluded (is_excluded=true)
    // dragging Included → Excluded: show indicator below (moving down)
    // dragging Excluded → Included: show indicator above (moving up)
    // BUT: if dropping on last row, always insert below; if first row, always insert above
    if (isLastInSection) {
      shouldInsertBelow = true;
    } else if (isFirstInSection) {
      shouldInsertBelow = false;
    } else {
      shouldInsertBelow = !draggedRow.is_excluded;
    }
  }

  // when inserting below, we want position + 1 to place it after the target
  const targetPosition = shouldInsertBelow
    ? dropTargetRow.position + 1
    : dropTargetRow.position;

  return {
    shouldInsertBelow,
    targetPosition,
  };
};
